import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { scrollToId } from "../config";

const navItems = [
  { key: "features", href: "#features" },
  { key: "manifesto", href: "#manifesto" },
  { key: "pricing", href: "#pricing" },
  { key: "faq", href: "#faq" },
];

export const Header = () => {
  const { lang, setLang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (href) => {
    setMenuOpen(false);
    setTimeout(() => scrollToId(href), 400);
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 1.9, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled
            ? "border-b border-white/10 bg-black/60 backdrop-blur-xl shadow-[0_8px_32px_rgba(0,0,0,0.4)]"
            : "border-b border-transparent bg-[#0A0A0A]/80"
        }`}
      >
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-6 lg:px-10">
          <button
            data-testid="header-logo"
            onClick={() => window.__lenis?.scrollTo(0, { duration: 1.4 })}
            className="font-bebas text-xl tracking-[0.18em] text-[#F5F5F5]"
          >
            METAL<span className="text-[#C5A059]">-CLASS</span>
          </button>

          <nav className="hidden items-center gap-8 lg:flex">
            {navItems.map((item) => (
              <button
                key={item.key}
                data-testid={`nav-link-${item.key}`}
                onClick={() => scrollToId(item.href)}
                className="group relative font-manrope text-[13px] font-medium uppercase tracking-[0.14em] text-[#8A9199] transition-colors duration-300 hover:text-[#F5F5F5]"
              >
                {t.nav[item.key]}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#C5A059] transition-[width] duration-300 group-hover:w-full" />
              </button>
            ))}
            <a
              data-testid="nav-link-reserved"
              href="/accedi"
              className="group relative font-manrope text-[13px] font-semibold uppercase tracking-[0.14em] text-[#C5A059] transition-colors duration-300 hover:text-[#F5F5F5]"
            >
              {t.nav.reserved}
              <span className="absolute -bottom-1 left-0 h-px w-full bg-[#C5A059]/40 transition-colors duration-300 group-hover:bg-[#F5F5F5]" />
            </a>
          </nav>

          <div className="flex items-center gap-4">
            <div
              data-testid="language-toggle"
              className="flex items-center border border-[#2A2A2A] bg-[#0A0A0A]"
            >
              {["it", "en"].map((l) => (
                <button
                  key={l}
                  data-testid={`lang-toggle-${l}`}
                  onClick={() => setLang(l)}
                  className={`px-3 py-1.5 font-bebas text-sm tracking-[0.2em] transition-colors duration-300 ${
                    lang === l
                      ? "bg-[#C5A059] text-[#0A0A0A]"
                      : "text-[#8A9199] hover:text-[#F5F5F5]"
                  }`}
                >
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
            <button
              data-testid="mobile-menu-button"
              onClick={() => setMenuOpen(true)}
              aria-label="Menu"
              className="p-1 text-[#F5F5F5] lg:hidden"
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            data-testid="mobile-menu"
            className="fixed inset-0 z-[70] flex flex-col bg-[#0A0A0A]/97 backdrop-blur-xl lg:hidden"
          >
            <div className="flex h-[72px] items-center justify-between border-b border-[#2A2A2A] px-6">
              <span className="font-bebas text-xl tracking-[0.18em] text-[#F5F5F5]">
                METAL<span className="text-[#C5A059]">-CLASS</span>
              </span>
              <button
                data-testid="mobile-menu-close"
                onClick={() => setMenuOpen(false)}
                aria-label="Chiudi menu"
                className="p-1 text-[#F5F5F5]"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8">
              {navItems.map((item, i) => (
                <motion.button
                  key={item.key}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.08 * i, ease: [0.16, 1, 0.3, 1] }}
                  data-testid={`mobile-nav-${item.key}`}
                  onClick={() => goTo(item.href)}
                  className="border-b border-[#1F1F1F] py-5 text-left font-bebas text-5xl uppercase tracking-tight text-[#F5F5F5] transition-colors duration-300 active:text-[#C5A059]"
                >
                  <span className="mr-4 font-cormorant text-2xl italic text-[#C5A059]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  {t.nav[item.key]}
                </motion.button>
              ))}
            </nav>
            <motion.a
              initial={{ opacity: 0, x: -24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.34, ease: [0.16, 1, 0.3, 1] }}
              data-testid="mobile-nav-reserved"
              href="/accedi"
              className="block border-b border-[#1F1F1F] py-5 text-left font-bebas text-5xl uppercase tracking-tight text-[#C5A059]"
            >
              <span className="mr-4 font-cormorant text-2xl italic text-[#C5A059]">05</span>
              {t.nav.reserved}
            </motion.a>
            <motion.button
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
              data-testid="mobile-nav-trial"
              onClick={() => goTo("#contatto")}
              className="mx-8 mb-8 mt-8 flex items-center justify-center bg-[#C5A059] px-8 py-4 font-manrope text-sm font-bold uppercase tracking-[0.14em] text-[#0A0A0A] transition-colors duration-300 active:bg-[#B38E4A]"
            >
              {t.hero.ctaPrimary}
            </motion.button>
            <p className="px-8 pb-10 font-manrope text-xs uppercase tracking-[0.2em] text-[#5C636A]">
              metal-class.com
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
